import { Fragment } from 'react';

import { isTimePast } from '@lib/server';
import type { SpecialTimetable, Times } from '@types';

import { Accordion } from './accordion';
import { Text } from './text';
import { Timetable, TimetableProps } from './timetable';

export interface OpeningTimesProps {
  showGeneral?: boolean;
  showSpecial?: boolean;
  times: Times;
}

export const defaultTitle = 'Opening Times';

export const OpeningTimes = ({ showGeneral = true, showSpecial = true, times }: OpeningTimesProps) => {
  const { global, timetable, special, notes } = times?.openingTimes || {};

  let openingTimes: TimetableProps[] = global ? [{ name: defaultTitle, content: timetable, special, notes }] : [];

  const extraTimetableData = (times.extraTimetables || [])
    .map(({ timetable, title }) =>
      timetable.global
        ? {
            name: title,
            content: timetable.timetable,
            notes: timetable.notes,
            special: timetable.special,
            type: timetable.type?.map((option) => option.label),
          }
        : null,
    )
    .filter(Boolean) as TimetableProps[];

  openingTimes.push(...extraTimetableData);

  openingTimes = openingTimes.map((timetable) => ({
    ...timetable,
    content: showGeneral
      ? timetable.content?.sort((a, b) => {
          const dayA = a.dayOfWeekNumber === 0 ? 7 : a.dayOfWeekNumber || 0;
          const dayB = b.dayOfWeekNumber === 0 ? 7 : b.dayOfWeekNumber || 0;
          return dayA - dayB;
        })
      : [],
    special: showSpecial ? timetable.special || [] : [],
  }));

  if (!openingTimes?.length) {
    return null;
  }

  const isSpecialValid = (special: SpecialTimetable) =>
    special?.openingTimes?.[0]?.date && isTimePast(special?.openingTimes?.[0]?.date);

  return (
    <Accordion
      type={'single'}
      defaultValue={openingTimes[0].name}
      items={openingTimes.map((timetable) => ({
        title: timetable.name,
        content: (
          <Fragment key={timetable.name}>
            {/* General and Special enabled */}
            {showGeneral &&
              showSpecial &&
              (timetable.special?.filter((special) => isSpecialValid(special))?.length ? (
                <Accordion
                  type={'single'}
                  className="border-border/50 border-l-2 pt-0 pl-4"
                  defaultValue="General"
                  items={[
                    {
                      title: 'General',
                      content: <Timetable timetable={timetable} />,
                    },
                    {
                      title: 'Special',
                      content: <Timetable special timetable={timetable} />,
                    },
                  ]}
                />
              ) : (
                <Timetable timetable={timetable} />
              ))}

            {/* Only general enabled */}
            {showGeneral && !showSpecial && <Timetable timetable={timetable} />}

            {/* Only special enabled */}
            {showSpecial &&
              !showGeneral &&
              (timetable.special?.filter((special) => isSpecialValid(special))?.length ? (
                <Timetable special timetable={timetable} />
              ) : (
                <Text element={'span'} className={'text-muted-foreground! text-sm'}>
                  No special {timetable.name} available
                </Text>
              ))}

            {/* No general or special enabled */}
            {!showGeneral && !showSpecial && (
              <Text element={'span'} className={'text-muted-foreground! text-sm'}>
                No {timetable.name} available
              </Text>
            )}
          </Fragment>
        ),
      }))}
    />
  );
};
